import { useSyncExternalStore } from "react";
import type { DashboardSnapshot, LiveBlock, StreamGap } from "../../shared/types";
import { ApiRequestError, getDashboard } from "../api";

export interface LiveState {
  dashboard: DashboardSnapshot | null;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  lastAlert: LiveBlock | null;
  connection: "connecting" | "live" | "reconnecting";
  streamGaps: StreamGap[];
}

let state: LiveState = {
  dashboard: null,
  loading: true,
  refreshing: false,
  error: null,
  lastAlert: null,
  connection: "connecting",
  streamGaps: [],
};
let eventSource: EventSource | null = null;
let activeRequest: AbortController | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let visibilityListening = false;
let hiddenUpdatePending = false;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

function emit(next: Partial<LiveState>, silent = false): void {
  state = { ...state, ...next };
  if (silent) {
    hiddenUpdatePending = true;
    return;
  }
  notify();
}

export function mergeSessionBlocks(current: LiveBlock[], incoming: LiveBlock[], limit = 50): LiveBlock[] {
  const byNumber = new Map(current.map((block) => [block.blockNumber, block]));
  for (const block of incoming) byNumber.set(block.blockNumber, block);
  return [...byNumber.values()]
    .sort((left, right) => left.blockNumber === right.blockNumber ? 0 : BigInt(left.blockNumber) < BigInt(right.blockNumber) ? -1 : 1)
    .slice(-limit);
}

function isPageHidden(): boolean {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

function onVisibilityChange(): void {
  if (!isPageHidden() && hiddenUpdatePending) {
    hiddenUpdatePending = false;
    notify();
  }
}

function applyBlocks(blocks: LiveBlock[], silent = isPageHidden()): void {
  if (!blocks.length) return;
  const dashboard = state.dashboard;
  const liveBlocks = dashboard
    ? mergeSessionBlocks(dashboard.liveBlocks, blocks, 60).reverse()
    : [];
  const alert = [...blocks].reverse().find((block) => block.severity !== "routine") ?? state.lastAlert;
  emit({
    dashboard: dashboard ? { ...dashboard, liveBlocks } : dashboard,
    lastAlert: alert,
    connection: "live",
  }, silent);
}

async function load(initial = false): Promise<void> {
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
  activeRequest?.abort();
  activeRequest = new AbortController();
  emit({ loading: initial || !state.dashboard, refreshing: !initial && Boolean(state.dashboard), error: null }, isPageHidden());
  try {
    const dashboard = await getDashboard(activeRequest.signal);
    emit({ dashboard, loading: false, refreshing: false, error: null }, isPageHidden());
  } catch (error) {
    if ((error as Error).name === "AbortError") return;
    const retryable = error instanceof ApiRequestError && error.status === 503;
    emit({
      loading: retryable && !state.dashboard,
      refreshing: retryable && Boolean(state.dashboard),
      error: error instanceof Error ? error.message : String(error),
    }, isPageHidden());
    if (retryable) {
      retryTimer = setTimeout(() => {
        retryTimer = null;
        void load(!state.dashboard);
      }, (error.retryAfterSeconds ?? 3) * 1_000);
    }
  }
}

function connect(): void {
  if (eventSource || typeof EventSource === "undefined") return;
  eventSource = new EventSource("/api/live");
  eventSource.addEventListener("connected", () => emit({ connection: "live" }, isPageHidden()));
  eventSource.addEventListener("block", (event) => {
    const block = JSON.parse((event as MessageEvent<string>).data) as LiveBlock;
    applyBlocks([block]);
  });
  eventSource.addEventListener("gap", (event) => {
    const gap = JSON.parse((event as MessageEvent<string>).data) as StreamGap;
    emit({ streamGaps: [...state.streamGaps, gap].slice(-8), connection: "live" }, isPageHidden());
    void load();
  });
  eventSource.addEventListener("refresh", () => {
    void load();
  });
  eventSource.onerror = () => emit({ connection: "reconnecting" }, isPageHidden());
}

export const liveStore = {
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    if (listeners.size === 1) {
      void load(true);
      connect();
      if (typeof document !== "undefined" && !visibilityListening) {
        document.addEventListener("visibilitychange", onVisibilityChange);
        visibilityListening = true;
      }
    }
    return () => {
      listeners.delete(listener);
      if (!listeners.size) {
        if (retryTimer) clearTimeout(retryTimer);
        retryTimer = null;
        activeRequest?.abort();
        activeRequest = null;
        eventSource?.close();
        eventSource = null;
        if (typeof document !== "undefined" && visibilityListening) {
          document.removeEventListener("visibilitychange", onVisibilityChange);
          visibilityListening = false;
        }
      }
    };
  },
  getSnapshot(): LiveState {
    return state;
  },
  getServerSnapshot(): LiveState {
    return state;
  },
  refresh(): void {
    void load();
  },
};

export function useLiveDashboard(): LiveState {
  return useSyncExternalStore(liveStore.subscribe, liveStore.getSnapshot, liveStore.getServerSnapshot);
}
